<template>
  <div class="promo-banners">
    <!-- Arcade Banner -->
    <div class="arcade-banner">
      <div class="banner-content">
        <div class="banner-icon">
          <i class="fa-solid fa-gamepad"></i>
        </div>
        <div class="banner-text">
          <h3>{{ t('PROMO_BANNERS.arcade.title') }}</h3>
          <p>{{ t('PROMO_BANNERS.arcade.description') }}</p>
        </div>
        <RouterLink class="banner-cta" to="/arcade">
          {{ t('PROMO_BANNERS.arcade.cta') }}
        </RouterLink>
      </div>
    </div>

    <!-- Giveaway Banner -->
    <div class="giveaway-banner">
      <div class="banner-content">
        <div class="banner-icon">
          <i class="fa-solid fa-gift"></i>
        </div>
        <div class="banner-text">
          <h3>{{ t('PROMO_BANNERS.giveaway.title') }}</h3>
          <p>{{ t('PROMO_BANNERS.giveaway.description') }}</p>
        </div>
        <RouterLink class="banner-cta" :to="{ name: RouteName.GIVEAWAYS }">
          {{ t('PROMO_BANNERS.giveaway.cta') }}
        </RouterLink>
      </div>
    </div>


  </div>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { RouteName } from '@/router/types'

const { t } = useI18n()
</script>

<style scoped lang="scss">
.promo-banners {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 0 auto 2rem auto;
  width: 100%;

  @include tablet {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  @include mobile {
    padding: 0 0.5rem;
  }
}

.daily-free-banner,
.arcade-banner,
.giveaway-banner {
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.daily-free-banner {
  height: 54px;
  background:
    linear-gradient(
      135deg,
      transparent 0%,
      rgba(186, 85, 211, 0.25) 25%,
      transparent 50%,
      rgba(138, 43, 226, 0.22) 75%,
      transparent 100%
    ),
    linear-gradient(
      45deg,
      rgba(147, 51, 234, 0.15) 0%,
      transparent 25%,
      rgba(186, 85, 211, 0.18) 50%,
      transparent 75%,
      rgba(138, 43, 226, 0.15) 100%
    ),
    radial-gradient(ellipse at 20% 35%, rgba(186, 85, 211, 0.3) 0%, transparent 70%),
    radial-gradient(ellipse at 80% 75%, rgba(138, 43, 226, 0.25) 0%, transparent 70%),
    linear-gradient(90deg, #5a4fcf 0%, #7c3aed 50%, #5a4fcf 100%);
  background-size:
    160% 160%,
    120% 120%,
    80px 40px,
    80px 40px,
    100% 100%;
  animation: amethystShimmer 50s ease-in-out infinite;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(155, 89, 182, 0.04) 50%,
      transparent 100%
    );
    animation: amethystSweep 6s ease-in-out infinite;
    pointer-events: none;
  }

  @include mobile {
    height: 54px;
  }

  &.disabled {
    background:
      linear-gradient(
        135deg,
        transparent 0%,
        rgba(108, 117, 125, 0.08) 25%,
        transparent 50%,
        rgba(90, 98, 104, 0.06) 75%,
        transparent 100%
      ),
      linear-gradient(
        45deg,
        rgba(73, 80, 87, 0.04) 0%,
        transparent 25%,
        rgba(108, 117, 125, 0.05) 50%,
        transparent 75%,
        rgba(90, 98, 104, 0.04) 100%
      ),
      radial-gradient(ellipse at 20% 35%, rgba(108, 117, 125, 0.1) 0%, transparent 70%),
      radial-gradient(ellipse at 80% 75%, rgba(90, 98, 104, 0.08) 0%, transparent 70%),
      linear-gradient(90deg, #2c2c2c 0%, #3c3c3c 50%, #2c2c2c 100%);
    background-size:
      160% 160%,
      120% 120%,
      80px 40px,
      80px 40px,
      100% 100%;
    animation: amethystShimmer 50s ease-in-out infinite;

    &::before {
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(108, 117, 125, 0.02) 50%,
        transparent 100%
      );
      animation: amethystSweep 6s ease-in-out infinite;
    }

    .banner-cta:disabled {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.5);
      cursor: not-allowed;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.1);
      }
    }
  }
}

.arcade-banner {
  height: 80px;
  background:
    linear-gradient(
      135deg,
      transparent 0%,
      rgba(255, 165, 0, 0.25) 25%,
      transparent 50%,
      rgba(255, 140, 0, 0.22) 75%,
      transparent 100%
    ),
    linear-gradient(
      45deg,
      rgba(255, 140, 0, 0.15) 0%,
      transparent 25%,
      rgba(255, 165, 0, 0.18) 50%,
      transparent 75%,
      rgba(255, 140, 0, 0.15) 100%
    ),
    radial-gradient(ellipse at 25% 30%, rgba(255, 165, 0, 0.3) 0%, transparent 65%),
    radial-gradient(ellipse at 75% 70%, rgba(255, 140, 0, 0.25) 0%, transparent 65%),
    linear-gradient(90deg, #e65100 0%, #ff9800 50%, #e65100 100%);
  background-size:
    180% 180%,
    140% 140%,
    100px 70px,
    100px 70px,
    100% 100%;
  animation: emeraldShimmer 55s ease-in-out infinite;
  position: relative;
  border-radius: 8px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 100%
    );
    animation: emeraldSweep 7s ease-in-out infinite;
    pointer-events: none;
  }

  @include mobile {
    height: 70px;
  }
}

.giveaway-banner {
  height: 80px;
  background:
    linear-gradient(
      135deg,
      rgba(59, 130, 246, 0.18) 0%,
      transparent 45%,
      rgba(14, 165, 233, 0.15) 75%,
      transparent 100%
    ),
    linear-gradient(
      45deg,
      rgba(29, 78, 216, 0.12) 0%,
      transparent 30%,
      rgba(37, 99, 235, 0.12) 60%,
      transparent 90%,
      rgba(56, 189, 248, 0.12) 100%
    ),
    radial-gradient(ellipse at 30% 40%, rgba(59, 130, 246, 0.22) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 80%, rgba(14, 165, 233, 0.18) 0%, transparent 60%),
    linear-gradient(90deg, #2563eb 0%, #38bdf8 50%, #2563eb 100%);
  background-size:
    200% 200%,
    150% 150%,
    120px 80px,
    120px 80px,
    100% 100%;
  animation: luxuryShimmer 60s ease-in-out infinite;
  position: relative;
  border-radius: 8px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(125, 211, 252, 0.08) 45%,
      transparent 100%
    );
    animation: goldSweep 8s ease-in-out infinite;
    pointer-events: none;
  }

  @include mobile {
    height: 70px;
  }
}



@keyframes gradientShift {
  0%,
  100% {
    background-position:
      0% 50%,
      0% 0%;
  }

  50% {
    background-position:
      100% 50%,
      100% 100%;
  }
}

@keyframes conicSpin {
  0% {
    background-position: 0% 50%;
  }

  100% {
    background-position: 100% 50%;
  }
}

@keyframes stripeSlide {
  0% {
    background-position: 0% 0%;
  }

  100% {
    background-position: 240px 0%;
  }
}

@keyframes luxuryShimmer {
  0%,
  100% {
    background-position:
      0% 0%,
      0% 0%,
      0% 0%,
      0% 0%,
      0% 0%;
  }

  25% {
    background-position:
      100% 100%,
      50% 50%,
      20px 10px,
      -20px -10px,
      0% 0%;
  }

  50% {
    background-position:
      200% 0%,
      100% 100%,
      40px 20px,
      20px 40px,
      0% 0%;
  }

  75% {
    background-position:
      100% 200%,
      150% 50%,
      10px 30px,
      30px 10px,
      0% 0%;
  }
}

@keyframes goldSweep {
  0%,
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }

  50% {
    transform: translateX(100%);
    opacity: 1;
  }
}

@keyframes emeraldShimmer {
  0%,
  100% {
    background-position:
      0% 0%,
      0% 0%,
      0% 0%,
      0% 0%,
      0% 0%;
  }

  25% {
    background-position:
      90% 90%,
      45% 45%,
      15px 8px,
      -15px -8px,
      0% 0%;
  }

  50% {
    background-position:
      180% 0%,
      90% 90%,
      30px 15px,
      15px 30px,
      0% 0%;
  }

  75% {
    background-position:
      90% 180%,
      135% 45%,
      8px 25px,
      25px 8px,
      0% 0%;
  }
}

@keyframes emeraldSweep {
  0%,
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }

  50% {
    transform: translateX(100%);
    opacity: 1;
  }
}

@keyframes amethystShimmer {
  0%,
  100% {
    background-position:
      0% 0%,
      0% 0%,
      0% 0%,
      0% 0%,
      0% 0%;
  }

  25% {
    background-position:
      80% 80%,
      40% 40%,
      12px 6px,
      -12px -6px,
      0% 0%;
  }

  50% {
    background-position:
      160% 0%,
      80% 80%,
      24px 12px,
      12px 24px,
      0% 0%;
  }

  75% {
    background-position:
      80% 160%,
      120% 40%,
      6px 20px,
      20px 6px,
      0% 0%;
  }
}

@keyframes amethystSweep {
  0%,
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }

  50% {
    transform: translateX(100%);
    opacity: 1;
  }
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 1.5rem;

  @include mobile {
    padding: 0 0.75rem;
  }
}

.banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  flex-shrink: 0;

  i {
    font-size: 1.2rem;
    color: white;
  }

  @include mobile {
    display: none;
  }
}

.banner-text {
  flex: 1;
  margin-left: 1rem;
  color: white;

  @include mobile {
    margin-left: 0;
    margin-right: 8px;
  }

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);

    @include desktop {
      font-size: 1rem;
    }
  }

  p {
    font-size: 0.9rem;
    margin: 0;
    opacity: 0.9;
    line-height: 1.2;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);

    @include mobile {
      font-size: 0.7rem;
    }
  }
}

.banner-cta {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
    transform: scale(0.98);
  }

  @include mobile {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
}
</style>
